import {
  DateRange,
  DayHour,
  DayMaskDefinition,
  DayNumber,
  EnabledDateRange,
  getDayMaskDayFromDayNumber,
  getDayNumberFromDayMaskKey,
  getOverlapLength,
  MINS_DAY,
  MINS_HR,
  MINS_WEEK,
  MinuteNumber,
  NumberRange,
  UTCDayMaskPeriod,
  utcDayMaskPeriodToUtcDayPeriods,
  UTCDayPeriod,
  utcDayPeriodsToUtcDayMaskPeriod,
  UTCPeriodHrMins,
  UTCSingleTimePoint,
} from '@iotv/iotv-v3-types'

import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../lambda'
import { UTCHrMin, InsertBehavior } from '@iotv/iotv-v3-types'
import { capitalize } from '@iotv/core-services'
import { getNiceDate } from './millisecondOperations'


const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

/** get from period. NOT taking into account out of day utc_start_hour (>23) or mins ( >59) */
export const getFromMins = ({ utc_start_hour, utc_start_min }: UTCPeriodHrMins) => utc_start_hour * MINS_HR + utc_start_min
export const getToMins = (utcPeriodHrMins: UTCPeriodHrMins) => getFromMins(utcPeriodHrMins) + utcPeriodHrMins.duration_mins

/**
 * For display purposes, get the padded `HH:mm` string from hours and minutes (not UTS or locale aware)
 */
export const printHrDay = (hr: number, min: number) => `${hr.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`

/**
 * For debug and display purposes
 */
export const printDateRange = (dr: DateRange) => {
  const intl: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    weekday: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }
  return `${getNiceDate(dr[0].valueOf(), intl)} -> ${getNiceDate(dr[1].valueOf(), intl)}`
}

/**
 * For display purposes, get the padded `HH:mm` string from a `UTCPeriodHrMins` timeslot
 */
export const printUTimeslot = (timeslot: UTCPeriodHrMins) => `${printHrDay(timeslot.utc_start_hour, timeslot.utc_start_min)} (${timeslot.duration_mins}min)`

/**
 * For display purposes, get padded `Day HH:mm` string from a `UTCPeriodHrMins` timeslot
 */
export const printUTCDayPeriod = (p: UTCDayPeriod, short?: boolean) => {
  const {
    p: { utc_start_hour: local_start_hour, utc_start_min: local_start_min },
    utcDay,
  } = p

  const day = capitalize(getDayMaskDayFromDayNumber(utcDay))
  const printDay = short ? day.substring(0, 3) : day
  return `${printDay} ${printHrDay(local_start_hour, local_start_min)}`
}

type UTCPeriodsOverlapInput = {
  periodA: UTCDayPeriod
  periodB: UTCDayPeriod
}

/** returns true if period A and B overlap, taking into account either or both A and B spanning the day 6 to day 0 changeover  */
export const utcPeriodOverlaps = ({ periodA, periodB }: UTCPeriodsOverlapInput): boolean => {
  const x1DayMinsStart = getRelativeUTCDayMaskStart(periodA)
  const x1DayMinsEnd = getRelativeUTCDayMaskEnd(periodA)
  const x2DayMinsStart = getRelativeUTCDayMaskStart(periodB)
  const x2DayMinsEnd = getRelativeUTCDayMaskEnd(periodB)

  const periodASlots: NumberRange[] = []

  if (x1DayMinsEnd < x1DayMinsStart) {
    const firstPeriodA: NumberRange = [x1DayMinsStart, MINS_WEEK]
    const secondPeriodA: NumberRange = [0, x1DayMinsEnd]
    debug && console.log(`Period A spans new week`, { firstPeriodA, secondPeriodA })
    periodASlots.push(firstPeriodA, secondPeriodA)
  } else {
    const origPeriodA: NumberRange = [x1DayMinsStart, x1DayMinsEnd]
    periodASlots.push(origPeriodA)
  }

  const periodBSlots: NumberRange[] = []

  if (x2DayMinsEnd < x2DayMinsStart) {
    const firstPeriodB: NumberRange = [x2DayMinsStart, MINS_WEEK]
    const secondPeriodB: NumberRange = [0, x2DayMinsEnd]
    debug && console.log(`Period B spans new week`, { firstPeriodB, secondPeriodB })
    periodBSlots.push(firstPeriodB, secondPeriodB)
  } else {
    const origPeriodB: NumberRange = [x2DayMinsStart, x2DayMinsEnd]
    periodBSlots.push(origPeriodB)
  }

  const totalOverlap = periodASlots.reduce((aAcc, aCurr) => {
    const totalBLevelOverlap = periodBSlots.reduce((bAcc, bCurr) => {
      const bLevelOverlep = getOverlapLength([aCurr, bCurr])
      debug && console.log(`BLevelOverlap`, { aCurr, bCurr }, bLevelOverlep)
      return bAcc + bLevelOverlep
    }, 0)
    debug && console.log(`totalBLevelOverlap`, totalBLevelOverlap)
    return aAcc + totalBLevelOverlap
  }, 0)

  debug && console.log('Total overlap ', totalOverlap)

  return totalOverlap !== 0
}

/** return number of minutes from start of utc week for the start part of a period, given a utc week start day */
export const getRelativeUTCDayMaskStart = ({ p, utcDay }: UTCDayPeriod) => {
  /** the minutes from start of 7 day period which may be more than on period ahead */
  const zeroAbsoluteStartMins = utcDay * MINS_DAY + getFromMins(p)
  /** the equivalent minutes in an arbitrary period */
  const zeroRelativeStartMins = zeroAbsoluteStartMins % MINS_WEEK
  return zeroRelativeStartMins
}

/** return number of minutes from start of utc week for the end part of a period, given a utc week start day */
export const getRelativeUTCDayMaskEnd = ({ p, utcDay }: UTCDayPeriod) => {
  const { duration_mins } = p
  /** the minutes from start of 7 day period which may be more than on period ahead */
  const zeroAbsoluteEndMins = getRelativeUTCDayMaskStart({ p, utcDay }) + duration_mins
  /** the equivalent minutes in an arbitrary period */
  const zeroRelativeEndMins = zeroAbsoluteEndMins % MINS_WEEK
  return zeroRelativeEndMins
}

/** given minutes from start of a utc week, return week day, hour and min with keys in a form which is DayMaskDefintion compatible */
export const getDayMaskFromWeekRelativeUTCMins = (mins: number): UTCSingleTimePoint => {
  const days = Math.floor(mins / MINS_DAY) as DayNumber
  const dayNumber = days % 7
  const remainingMinsA = mins - days * MINS_DAY
  const utcHour = Math.floor(remainingMinsA / MINS_HR) as DayHour
  const utcMin = (remainingMinsA - utcHour * MINS_HR) as MinuteNumber
  const dayMaskDay = getDayMaskDayFromDayNumber(dayNumber)
  return { dayMaskDay, utcHour, utcMin }
}

type ShiftUTCPeriodByMinsInput = {
  utcPeriod: UTCDayPeriod
  /** a positive or negative shift in minutes, which if not integer will be processed as Math.floor */
  shiftMins: number
}

/** shift utcDayPeriod positively or negatively by shiftMins minutes. Use this to manipuate timeslots of an IEntityProgrammme */
export const shiftUTCDayPeriodByMins = ({ utcPeriod, shiftMins }: ShiftUTCPeriodByMinsInput): UTCDayPeriod => {
  const {
    p: { duration_mins },
  } = utcPeriod
  const relativeStartMins = getRelativeUTCDayMaskStart(utcPeriod)
  const newRelativeStartMins = relativeStartMins + shiftMins
  const { dayMaskDay, utcHour, utcMin } = getDayMaskFromWeekRelativeUTCMins(newRelativeStartMins)
  return { utcDay: getDayNumberFromDayMaskKey(dayMaskDay), p: { duration_mins, utc_start_hour: utcHour, utc_start_min: utcMin } }
}

type ShiftUTCDayMaskPeriodByMinsInput = {
  utcDayMaskPeriod: UTCDayMaskPeriod
  /** a positive or negative shift in minutes, which if not integer will be processed as Math.floor */
  shiftMins: number
}

/** shift full utcDayMaskPeriod positively or negatively by shiftMins minutes. Use this to shift a full dayMask plus duration, start hr and start min object */
export const shiftUTCDayMaskPeriodByMins = ({ utcDayMaskPeriod, shiftMins }: ShiftUTCDayMaskPeriodByMinsInput): UTCDayMaskPeriod => {
  const uTCDayPeriods: UTCDayPeriod[] = utcDayMaskPeriodToUtcDayPeriods(utcDayMaskPeriod).map((utcPeriod) => shiftUTCDayPeriodByMins({ utcPeriod, shiftMins }))
  return utcDayPeriodsToUtcDayMaskPeriod(uTCDayPeriods)
}

/** shifts period by shiftMins, does NOT reset utc_start_hour to be relative to 0 if addition causes hr to be more than 23 */
export const shiftUTCPeriodHrMins = (uTCPeriodHrMins: UTCPeriodHrMins, shiftMins: number): UTCPeriodHrMins => {
  const duration_mins = uTCPeriodHrMins.duration_mins
  const mins = getFromMins(uTCPeriodHrMins)
  const shfitedMins = mins + shiftMins
  const utc_start_hour = Math.floor(shfitedMins / MINS_HR)
  const utc_start_min = shfitedMins - utc_start_hour * MINS_HR
  return {
    duration_mins,
    utc_start_hour,
    utc_start_min,
  }
}

type GetUTCPeriodsForUtcRangeInput = {
  duration_mins: number
  start: UTCHrMin
  max: UTCHrMin
}
export function* utcPeriodGenerator({ start, duration_mins, max }: GetUTCPeriodsForUtcRangeInput) {
  const { utcHour: utc_start_hour, utcMin: utc_start_min } = start
  let thisPeriod: UTCPeriodHrMins = { duration_mins, utc_start_hour, utc_start_min }
  let nextPeriod = shiftUTCPeriodHrMins(thisPeriod, duration_mins)
  const maxPeriod: UTCPeriodHrMins = { duration_mins, utc_start_hour: max.utcHour, utc_start_min: max.utcMin }
  let err: Error | null = null
  do {
    try {
      yield thisPeriod
      thisPeriod = nextPeriod
      nextPeriod = shiftUTCPeriodHrMins(nextPeriod, duration_mins)
    } catch (e: any) {
      err = e
      console.log('Error in stateMutator', e.message)
    }
  } while (!err && getFromMins(nextPeriod) <= getToMins(maxPeriod))
}

export const getUTCPeriodsForUtcRange = ({ start, duration_mins, max }: GetUTCPeriodsForUtcRangeInput): UTCPeriodHrMins[] => {
  const arr: UTCPeriodHrMins[] = []
  const periods = utcPeriodGenerator({ duration_mins, max, start })
  for (let next of periods) {
    arr.push(next)
  }
  return arr
}

type InsertUTCPeriodHrMinsIntoArrayInput = {
  newTimeslot: UTCPeriodHrMins
  timeslots: UTCPeriodHrMins[]
  insertBehavior: InsertBehavior
}

/** insert timeslot in timeslot array before next most immediate start timeslot, shifting those later by the duration of the inserted timeslot if the new timeslot would overlap the next otherwises
 * any shifted timeslots exceeding start times of 23:59 are removed
 */
export const insertUTCPeriodHrMinsIntoArray = ({ newTimeslot, timeslots, insertBehavior }: InsertUTCPeriodHrMinsIntoArrayInput) => {
  const newTimeslotStart = getFromMins(newTimeslot)
  const idx = timeslots.findIndex((t) => {
    const tStart = getFromMins(t)
    return newTimeslotStart <= tStart
  })

  if (insertBehavior === InsertBehavior.SHIFT) {
    timeslots.splice(idx, 0, newTimeslot)
    for (let i = idx; i < timeslots.length; i++) {
      const last = timeslots.at(i - 1)
      const next = timeslots.at(i)
      if (last && next) {
        const overlapMins = getToMins(last) - getFromMins(next)
        const overlaps = overlapMins > 0
        if (overlaps) {
          timeslots[i] = shiftUTCPeriodHrMins(next, overlapMins)
        }
      }
    }
  } else if (insertBehavior === InsertBehavior.TRUNCATE) {
    const next = timeslots.at(idx)
    if (next) {
      const nextFrom = getFromMins(next)
      const duration_mins = nextFrom - newTimeslotStart
      timeslots.splice(idx, 0, { ...newTimeslot, duration_mins })
    }
  }
  const inDayTimeslots = timeslots.filter((t) => t.utc_start_hour < 24)
  return inDayTimeslots
}

type UtcPeriodHrMinToDateRangesInput = {
  dayMask: DayMaskDefinition
  relativeDate: Date
  utcPeriodHrMins: UTCPeriodHrMins
}

/** returns sorted array of date periods, sorted by start time, starting from relativeDate for one week cycle */
export const utcPeriodHrMinToDateRanges = ({ relativeDate, utcPeriodHrMins, dayMask }: UtcPeriodHrMinToDateRangesInput): DateRange[] => {
  let unorderedDateRanges: DateRange[] = []

  const utcDay = relativeDate.getUTCDay()
  const { duration_mins, utc_start_hour, utc_start_min } = utcPeriodHrMins
  const { sunday, monday, tuesday, wednesday, thursday, friday, saturday } = dayMask
  /** Sunday = 0 */
  const CanonicalDayMask = [sunday, monday, tuesday, wednesday, thursday, friday, saturday]

  for (let absIdx = utcDay; absIdx < utcDay + CanonicalDayMask.length; absIdx++) {
    /** pick the day from the day mask via Canonical position in week */
    const relIdx = absIdx % CanonicalDayMask.length
    const dayEnabled = CanonicalDayMask[relIdx]

    if (dayEnabled) {
      /** start from relative date and increment for 1 week */
      const sevenDayCycleIdx = absIdx - utcDay
      const baseStart = new Date(relativeDate)
      baseStart.setUTCDate(baseStart.getUTCDate() + sevenDayCycleIdx)
      baseStart.setUTCHours(utc_start_hour, utc_start_min, 0, 0)
      const baseEnd = new Date(baseStart)
      baseEnd.setUTCMinutes(baseEnd.getUTCMinutes() + duration_mins)
      debug &&
        console.log({
          absIdx,
          day: Object.keys({ sunday, monday, tuesday, wednesday, thursday, friday, saturday })[relIdx],
          relIdx,
          dayEnabled,
          start: baseStart.toUTCString(),
          end: baseEnd.toUTCString(),
        })

      unorderedDateRanges.push([baseStart, baseEnd])
    }
  }

  let sortedDateRanges: DateRange[] = unorderedDateRanges
    .map((dateRange) => {
      return dateRange
    })
    .sort((a, b) => (a.at(0)! < b.at(0)! ? -1 : 1))

  return sortedDateRanges
}
